import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { 
  Copy, 
  Check, 
  History, 
  X,
  Save,
  Pencil
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { ChangeRecord, NoteFormat } from "@/types/productNotes";
import { Timestamp } from "firebase/firestore";
import { getUserById } from "@/services/userService";

interface ProductNotesCardProps {
  title: string;
  notes: string;
  /** Sanitized rich-text HTML (from the dashboard's Tiptap editor) to render instead of plain text. */
  html?: string;
  /** Author-chosen content format — "markdown" renders `notes` via ReactMarkdown instead of the html/plain-text paths. */
  format?: NoteFormat;
  onSave: (notes: string, historyRecord: Omit<ChangeRecord, "id">) => void;
  changeHistory: ChangeRecord[];
  showWhatsAppShare?: boolean;
  customerPhone?: string;
  disabled?: boolean;
  userId?: string;
}

export function ProductNotesCard({
  title,
  notes,
  html,
  format = "richtext",
  onSave,
  changeHistory,
  showWhatsAppShare = false,
  customerPhone,
  disabled = false,
  userId = "unknown",
}: ProductNotesCardProps) {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editValue, setEditValue] = useState(notes);
  const [copied, setCopied] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [userNames, setUserNames] = useState<Record<string, string>>({});

  // Fetch user names when history dialog opens
  useEffect(() => {
    if (!historyOpen || changeHistory.length === 0) return;

    const fetchUserNames = async () => {
      const uniqueUserIds = [...new Set(changeHistory.map(r => r.changedBy))];
      const names: Record<string, string> = {};

      await Promise.all(
        uniqueUserIds.map(async (userId) => {
          if (userId && !userNames[userId]) {
            const user = await getUserById(userId);
            if (user) {
              names[userId] = user.name;
            }
          }
        })
      );

      setUserNames(prev => ({ ...prev, ...names }));
    };

    fetchUserNames();
  }, [historyOpen, changeHistory]);

  const handleOpenEdit = () => {
    setEditValue(notes);
    setEditDialogOpen(true);
  };

  const handleSave = () => {
    if (editValue !== notes) {
      onSave(editValue, {
        content: editValue,
        changedBy: userId,
        changedAt: new Date(),
      });
    }
    setEditDialogOpen(false);
    toast.success("Notes saved successfully");
  };

  const handleCancel = () => {
    setEditValue(notes);
    setEditDialogOpen(false);
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(notes);
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleWhatsAppShare = () => {
    const encodedMessage = encodeURIComponent(notes);
    const phone = customerPhone?.replace(/\D/g, "") || "";
    const url = phone 
      ? `https://wa.me/91${phone}?text=${encodedMessage}`
      : `https://wa.me/?text=${encodedMessage}`;
    window.open(url, "_blank");
  };

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm md:text-base font-medium">{title}</CardTitle>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setHistoryOpen(true)}
                disabled={changeHistory.length === 0}
              >
                <History className="h-4 w-4" />
              </Button>
              {!disabled && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={handleOpenEdit}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleCopy}
                disabled={!notes}
              >
                {copied ? (
                  <Check className="h-4 w-4 text-success" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
              {showWhatsAppShare && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={handleWhatsAppShare}
                  disabled={!notes}
                  aria-label="Share via WhatsApp"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    className="h-4 w-4"
                    fill="#25D366"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {format === "markdown" && notes ? (
            <div className="prose prose-sm dark:prose-invert max-w-none min-h-[60px] md:min-h-[80px] break-words overflow-wrap-anywhere">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{notes}</ReactMarkdown>
            </div>
          ) : html ? (
            <div
              className="prose prose-sm dark:prose-invert max-w-none min-h-[60px] md:min-h-[80px] break-words overflow-wrap-anywhere"
              dangerouslySetInnerHTML={{ __html: html }}
            />
          ) : (
            <p className="text-sm text-muted-foreground whitespace-pre-wrap break-words overflow-wrap-anywhere min-h-[60px] md:min-h-[80px]">
              {notes || (disabled ? "No notes added yet." : "No notes added yet. Click the edit icon to add notes.")}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Edit Sheet */}
      <Sheet open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <SheetContent side="bottom" className="h-[70vh] rounded-t-3xl flex flex-col">
          <SheetHeader className="pb-4 flex-shrink-0">
            <SheetTitle className="text-xl font-bold">Edit {title}</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-1">
            <Textarea
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              placeholder="Add notes..."
              className="min-h-[200px] resize-none break-words"
              autoFocus
            />
          </div>
          <div className="flex gap-3 pt-4 pb-2 flex-shrink-0">
            <Button variant="outline" className="flex-1 h-12" onClick={handleCancel}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button className="flex-1 h-12" onClick={handleSave}>
              <Save className="h-4 w-4 mr-2" />
              Save
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* History Dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-md mx-4">
          <DialogHeader>
            <DialogTitle>Change History</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {changeHistory.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No change history available
              </p>
            ) : (
              changeHistory.map((record, index) => {
                const date = record.changedAt 
                  ? (record.changedAt instanceof Timestamp 
                      ? record.changedAt.toDate() 
                      : record.changedAt)
                  : new Date();
                return (
                <div
                  key={record.id || `history-${index}`}
                  className="p-3 rounded-lg bg-muted/50 space-y-2"
                >
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span className="font-medium">{userNames[record.changedBy] || record.changedBy}</span>
                    <span>
                      {date.toLocaleDateString()} at{" "}
                      {date.toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                  <p className="text-sm whitespace-pre-wrap">{record.content}</p>
                </div>
              )})
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}